/**
 * One modal handing off to the next with no scrim blink (Builders Modern
 * F1-e): the sender's exit beat is skipped entirely (it just stops
 * rendering) and the receiver FLIP-morphs its panel from the sender's last
 * on-screen rect, appearing with its scrim ALREADY at full opacity so the
 * dim backdrop never dips between the two.
 *
 * ── Receiver that is a FULL-SCREEN frame (KAN-766) ─────────────────────────
 * The invoice/estimate/report previews are full-screen frames, not centered
 * panels, so there is no panel rect to FLIP from. Such a receiver still calls
 * `useModalHandoffEnter(key)` (which consumes the staged entry) and, when
 * `skipEnter` is true, opens at rest with NO entrance motion
 * (`DocumentPreviewFrame instantEnter`): it covers the screen in the same frame
 * the sender unmounts, so the page beneath never shows. `morphEnterFromRect` is
 * ignored by it. Its exit beat is unaffected.
 *
 * ── Sender recipe (read this before wiring a hand-off) ────────────────────
 * 1. The sender already renders its shell as `AnimatedModalShell` with a
 *    `dialogRef={dialogElRef}` prop — `dialogElRef.current` is the panel DOM
 *    node that shell attaches via its own `dialogRef` prop.
 * 2. In the SAME handler that decides "swap to the next modal" (e.g. right
 *    after a successful create), call:
 *
 *      stageModalHandoff('invoice-builder-to-preview', dialogElRef.current);
 *      setBuilderOpen(false);   // <-- NOT guardedClose() / requestDismiss()
 *      setPreviewOpen(true);
 *
 *    Both state flips must land together, in the parent that conditionally
 *    renders `{builderOpen && <InvoiceBuilderModal .../>}` and
 *    `{previewOpen && <InvoicePreview .../>}`. The sender must hard-unmount
 *    — the whole `<InvoiceBuilderModal>` stops being rendered on the very
 *    next commit — rather than animate its own exit via
 *    `useBuilderModalShell`/`AnimatedModalShell`'s normal close path
 *    (`guardedClose`/`requestDismiss`). That is what "unmounts WITHOUT its
 *    own exit beat" means: don't fade the sender out — its
 *    `AnimatedModalShell` (and its scrim) simply isn't there on the next
 *    paint, at the same instant the receiver's IS there, so the two swap in
 *    one frame instead of one fading down while the other fades up (that gap
 *    is the "blink" this exists to prevent).
 * 3. The receiver reads `useModalHandoffEnter('invoice-builder-to-preview')`
 *    (from `src/hooks/ui/useModalHandoffEnter.js`) and spreads the result
 *    onto its own shell instance:
 *
 *      const { morphEnterFromRect, skipEnter } = useModalHandoffEnter('invoice-builder-to-preview');
 *      // AnimatedModalShell props: isOpen, morphEnterFromRect, skipEnter, ...
 *
 *    `skipEnter` is true whenever a hand-off was honored (this call consumed
 *    it) — it keeps BOTH the scrim's and the panel's own opacity/scale
 *    entrance from re-animating from 0, which is what "one constant scrim"
 *    actually requires: the receiver's scrim paints at full opacity on its
 *    very first frame, so across the sender→receiver swap the dim backdrop
 *    never dips. `morphEnterFromRect` (full motion only — see below) then
 *    layers the FLIP spring on TOP of that already-opaque scrim/panel via
 *    `AnimatedModalShell`'s existing `morphEnterFromRect` mechanism — the
 *    same one the bulk pill→modal reverse morph already uses. Nothing else
 *    to wire.
 * 4. Nothing staged / the ~1500ms TTL lapsed before the receiver reads it =>
 *    both fields come back `{ morphEnterFromRect: null, skipEnter: false }`
 *    and the receiver opens with its own normal entrance, unaffected.
 *
 * ── Reduced motion ─────────────────────────────────────────────────────────
 * `morphEnterFromRect` is nulled (no spatial FLIP spring — that IS motion),
 * but `skipEnter` is STILL true: the constant-scrim guarantee is a
 * correctness property (no blink), not a motion effect, and holds under
 * reduced motion too. The receiver's panel therefore also appears at its
 * resting opacity/position on the first frame — a plain, instant swap with
 * no scrim dip. A receiver that additionally wants a soft content-only
 * crossfade under reduced motion (rather than a hard swap) may wrap its own
 * children in a brief local opacity transition — that choice belongs to the
 * receiver, since `AnimatedModalShell`'s own panel opacity is pinned by
 * `skipEnter` for the no-blink guarantee and must not be repurposed for it.
 *
 * One-shot: a staged rect is consumed (removed) the FIRST time ANY
 * `useModalHandoffEnter(key)` reads it — a remount, a second builder reusing
 * the same key later, or a lost race all get the normal (non-hand-off) entry.
 */

/** How long a staged hand-off stays claimable before it's treated as stale. */
export const MODAL_HANDOFF_TTL_MS = 1500;

/** key -> { rect, at } */
const handoffStore = new Map();

function readRect(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  const r = el.getBoundingClientRect();
  if (!r || r.width <= 0 || r.height <= 0) return null;
  return {
    top: r.top, left: r.left, width: r.width, height: r.height,
  };
}

function sweepExpired(now) {
  handoffStore.forEach((entry, key) => {
    if (now - entry.at > MODAL_HANDOFF_TTL_MS) handoffStore.delete(key);
  });
}

/**
 * Called by the SENDER right before it hard-unmounts (see the recipe above).
 * Measures `dialogEl` (the sender's `AnimatedModalShell` panel node) and
 * stores its rect under `key`, replacing anything already staged there.
 *
 * @param {string} key - shared with the receiver's `useModalHandoffEnter(key)`
 * @param {Element|null} dialogEl - the sender's panel DOM node
 * @returns {{top:number,left:number,width:number,height:number}|null} the
 *   staged rect, or `null` if nothing measurable was given (e.g. the sender
 *   never mounted, or its ref wasn't attached yet) — a `null` return means no
 *   hand-off will be honored; the receiver opens normally.
 */
export function stageModalHandoff(key, dialogEl) {
  if (!key) return null;
  const now = Date.now();
  sweepExpired(now);
  const rect = readRect(dialogEl);
  if (!rect) return null;
  handoffStore.set(key, { rect, at: now });
  return rect;
}

/**
 * One-shot read: consumes (deletes) any entry staged under `key` and returns
 * its rect, or `null` if nothing is staged or it aged past `ttlMs`. Exported
 * separately from the React hook so the store's stage/take/TTL/one-shot
 * rules are unit-testable without rendering anything.
 *
 * @param {string} key
 * @param {{ ttlMs?: number, now?: number }} [opts] - override for tests
 */
export function takeModalHandoff(key, { ttlMs = MODAL_HANDOFF_TTL_MS, now = Date.now() } = {}) {
  const entry = handoffStore.get(key);
  if (!entry) return null;
  handoffStore.delete(key); // one-shot, regardless of freshness
  if (now - entry.at > ttlMs) return null;
  return entry.rect;
}

/** Test-only: clears every staged entry so specs don't leak into each other. */
export function __resetModalHandoffStoreForTests() {
  handoffStore.clear();
}
